// Portions of this code are adapted from the MUI Toolpad PageLayout component.
// Source: https://github.com/mui/toolpad/tree/master/packages/toolpad-core/src/PageContainer
// MUI is licensed under the MIT License: https://github.com/mui/toolpad/blob/master/LICENSE
import * as React from 'react';

import Box from '@mui/material/Box';
import Container, { ContainerProps } from '@mui/material/Container';
import { SxProps, useTheme } from '@mui/material/styles';

import { PageHeader, PageHeaderProps } from './PageHeader';

export interface Breadcrumb {
  /**
   * The title of the breadcrumb segment.
   */
  title: string;
  /**
   * The path the breadcrumb links to.
   */
  path?: string;
}
export interface PageContainerSlotProps {
  header: PageHeaderProps;
}

export interface PageContainerSlots {
  /**
   * The component that renders the page header.
   * @default PageHeader
   */
  header: React.ElementType;
}

export interface PageContainerProps extends ContainerProps {
  children?: React.ReactNode;
  /**
   * The title of the page. Leave blank to use the active page title.
   */
  title?: string;
  /**
   *Optional subtitle of the page.
   */
  subtitle?: string;
  /**
   * The breadcrumbs of the page. Leave blank to use the active page breadcrumbs.
   */
  breadcrumbs?: Breadcrumb[];
  /**
   * The components used for each slot inside.
   */
  slots?: PageContainerSlots;
  /**
   * The props used for each slot inside.
   */
  slotProps?: PageContainerSlotProps;
  /**
   * Makes the header sticky
   */
  stickyHeader?: boolean;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
}

/**
 * A container component to provide a title and breadcrumbs for your pages.
 *
 * Demos:
 *
 * - [Page Container](https://mui.com/toolpad/core/react-page-container/)
 *
 * API:
 *
 * - [PageContainer API](https://mui.com/toolpad/core/api/page-container)
 */
export function PageContainer(props: PageContainerProps) {
  const { children, breadcrumbs, slots, slotProps, title, subtitle, stickyHeader, ...rest } = props;
  const theme = useTheme();

  const PageHeaderSlot = slots?.header ?? PageHeader;

  // only show the page header when there is content to display
  const showPageHeader =
    Boolean(title) ||
    Boolean(subtitle) ||
    (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) ||
    Boolean(slotProps?.header && Object.keys(slotProps.header).length > 0) ||
    Boolean(slots?.header);

  return (
    <Container
      maxWidth={false}
      {...rest}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'auto',
        pb: 2,
        ...rest.sx,
      }}
    >
      <Box
        display={showPageHeader ? 'flex' : 'none'}
        // make header expand the full width and all the way to the top
        mx={-3}
        px={3}
        py={{ xs: 1, md: 2 }}
        // make header sticky
        {...(stickyHeader && {
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.default',
          zIndex: theme.zIndex.appBar,
        })}
      >
        <PageHeaderSlot title={title} subtitle={subtitle} breadcrumbs={breadcrumbs} {...slotProps?.header} />
      </Box>
      {children}
    </Container>
  );
}

export default PageContainer;
